import Api from './api';
import { Environment } from '../environment';
import { IStore, ICategory, IProduct } from '../types/Store';
import { IStoreStatus } from '../types/OpeningHours';

// Helper para construir URL da imagem (igual ao Gestor)
const getImageUrl = (key: string): string => {
    if (!key) return '';
    if (key.startsWith('http')) return key;
    return `${Environment.API_URL}/files/${key}`;
};

const getBySlug = async (slug: string): Promise<IStore> => {
    const { data } = await Api.get<any>(`/stores/slug/${slug}`);
    // Mapear _id para id se necessário
    return {
        ...data,
        id: data.id || data._id,
    };
};

const getCategories = async (storeId: string): Promise<ICategory[]> => {
    const { data } = await Api.get<{ success: boolean; count: number; categories: ICategory[] }>(`/categories/store/${storeId}?active=true`);
    const categories = (data.categories || []).map((cat: any) => ({
        ...cat,
        id: cat.id || cat._id,
        order: cat.order || 0
    })).filter((cat: any) => cat.active !== false);
    return categories.sort((a: any, b: any) => (a.order || 0) - (b.order || 0));
};

const getProducts = async (storeId: string): Promise<IProduct[]> => {
    const { data } = await Api.get<{ success: boolean; count: number; products: IProduct[] }>(`/products/store/${storeId}?active=true`);
    const products = (data.products || []).map((prod: any) => ({
        ...prod,
        id: prod.id || prod._id,
        category: typeof prod.category === 'object' ? prod.category._id || prod.category.id : prod.category,
        desc: prod.desc || prod.description || '',
        image: prod.image || prod.img || '',
        order: prod.order || 0
    })).filter((prod: any) => prod.active !== false);
    return products.sort((a: any, b: any) => (a.order || 0) - (b.order || 0));
};

const getStatus = async (storeId: string): Promise<IStoreStatus> => {
    const { data } = await Api.get<IStoreStatus>(`/opening-hours/status/${storeId}`);
    return data;
};

const getProfileStore = async (storeId: string): Promise<any> => {
    const { data } = await Api.get<any>(`/store/profile/${storeId}`);
    return data;
};

const getAddress = async (addressId: string): Promise<any> => {
    const { data } = await Api.get<any>(`/address/${addressId}`);
    return data;
};

const getSauces = async (storeId: string): Promise<any[]> => {
    try {
        const { data } = await Api.get<{ success: boolean; count: number; sauces: any[] }>(`/sauces?storeId=${storeId}&active=true`);
        return (data.sauces || []).map((s: any) => ({ ...s, id: s.id || s._id })).filter((s: any) => s.active !== false);
    } catch (error) { return []; }
};

const getSauceLists = async (storeId: string): Promise<any[]> => {
    try {
        const { data } = await Api.get<{ success: boolean; count: number; sauceLists: any[] }>(`/sauce-lists?storeId=${storeId}&active=true`);
        return (data.sauceLists || []).map((l: any) => ({ ...l, id: l.id || l._id })).filter((l: any) => l.active !== false);
    } catch (error) { return []; }
};

const getSideDishes = async (storeId: string): Promise<any[]> => {
    try {
        const { data } = await Api.get<{ success: boolean; count: number; sideDishes: any[] }>(`/sidedishes?storeId=${storeId}&active=true`);
        return (data.sideDishes || []).map((s: any) => ({ ...s, id: s.id || s._id })).filter((s: any) => s.active !== false);
    } catch (error) { return []; }
};

const getSideDishLists = async (storeId: string): Promise<any[]> => {
    try {
        const { data } = await Api.get<{ success: boolean; count: number; sideDishLists: any[] }>(`/sidedish-lists?storeId=${storeId}&active=true`);
        return (data.sideDishLists || []).map((l: any) => ({ ...l, id: l.id || l._id })).filter((l: any) => l.active !== false);
    } catch (error) { return []; }
};

const getAdditionals = async (storeId: string): Promise<any[]> => {
    try {
        const { data } = await Api.get<{ success: boolean; count: number; additionals: any[] }>(`/additionals?storeId=${storeId}&active=true`);
        return (data.additionals || []).map((s: any) => ({ ...s, id: s.id || s._id })).filter((s: any) => s.active !== false);
    } catch (error) { return []; }
};

const getAdditionalLists = async (storeId: string): Promise<any[]> => {
    try {
        const { data } = await Api.get<{ success: boolean; count: number; additionalLists: any[] }>(`/additional-lists?storeId=${storeId}&active=true`);
        return (data.additionalLists || []).map((l: any) => ({ ...l, id: l.id || l._id })).filter((l: any) => l.active !== false);
    } catch (error) { return []; }
};

const getFlavors = async (storeId: string): Promise<any[]> => {
    try {
        const { data } = await Api.get<{ success: boolean; count: number; flavors: any[] }>(`/flavors?storeId=${storeId}&active=true`);
        return (data.flavors || []).map((s: any) => ({ ...s, id: s.id || s._id })).filter((s: any) => s.active !== false);
    } catch (error) { return []; }
};

const getFlavorLists = async (storeId: string): Promise<any[]> => {
    try {
        const { data } = await Api.get<{ success: boolean; count: number; flavorLists: any[] }>(`/flavor-lists?storeId=${storeId}&active=true`);
        return (data.flavorLists || []).map((l: any) => ({ ...l, id: l.id || l._id })).filter((l: any) => l.active !== false);
    } catch (error) { return []; }
};

const getFlavorClassifications = async (storeId: string): Promise<any[]> => {
    try {
        const { data } = await Api.get<{ classifications: any[] }>(`/flavor-classifications?storeId=${storeId}`);
        return (data.classifications || []).map((c: any) => ({ ...c, id: c.id || c._id }));
    } catch (error) { return []; }
};

const getIngredients = async (storeId: string): Promise<any[]> => {
    try {
        const { data } = await Api.get<{ success: boolean; count: number; ingredients: any[] }>(`/ingredients?storeId=${storeId}&active=true`);
        return (data.ingredients || []).map((s: any) => ({ ...s, id: s.id || s._id })).filter((s: any) => s.active !== false);
    } catch (error) { return []; }
};

const getIngredientLists = async (storeId: string): Promise<any[]> => {
    try {
        const { data } = await Api.get<{ success: boolean; count: number; ingredientLists: any[] }>(`/ingredient-lists?storeId=${storeId}&active=true`);
        return (data.ingredientLists || []).map((l: any) => ({ ...l, id: l.id || l._id })).filter((l: any) => l.active !== false);
    } catch (error) { return []; }
};

const getSalads = async (storeId: string): Promise<any[]> => {
    try {
        const { data } = await Api.get<{ success: boolean; salads: any[] }>(`/salads?storeId=${storeId}&active=true`);
        return (data.salads || []).map((s: any) => ({ ...s, id: s.id || s._id })).filter((s: any) => s.active !== false);
    } catch (error) { return []; }
};

const getSaladLists = async (storeId: string): Promise<any[]> => {
    try {
        const { data } = await Api.get<{ success: boolean; saladLists: any[] }>(`/saladlists?storeId=${storeId}`);
        return (data.saladLists || []).map((l: any) => ({ ...l, id: l.id || l._id })).filter((l: any) => l.active !== false);
    } catch (error) { return []; }
};

const getDesserts = async (storeId: string): Promise<any[]> => {
    try {
        const { data } = await Api.get<{ success: boolean; desserts: any[] }>(`/desserts?storeId=${storeId}&active=true`);
        return (data.desserts || []).map((s: any) => ({ ...s, id: s.id || s._id })).filter((s: any) => s.active !== false);
    } catch (error) { return []; }
};

const getDessertLists = async (storeId: string): Promise<any[]> => {
    try {
        const { data } = await Api.get<{ success: boolean; dessertLists: any[] }>(`/dessertlists?storeId=${storeId}`);
        return (data.dessertLists || []).map((l: any) => ({ ...l, id: l.id || l._id })).filter((l: any) => l.active !== false);
    } catch (error) { return []; }
};

const getVariableDescriptions = async (storeId: string): Promise<any[]> => {
    try {
        const { data } = await Api.get<any[]>(`/variable-descriptions?storeId=${storeId}&active=true`);
        return (data || []).map((v: any) => ({ ...v, id: v._id || v.id })).filter((v: any) => v.active !== false);
    } catch (error) { return []; }
};

export const StoreService = {
    getBySlug,
    getCategories,
    getProducts,
    getStatus,
    getProfileStore,
    getAddress,
    getSauces,
    getSauceLists,
    getSideDishes,
    getSideDishLists,
    getAdditionals,
    getAdditionalLists,
    getFlavors,
    getFlavorLists,
    getFlavorClassifications,
    getIngredients,
    getIngredientLists,
    getSalads,
    getSaladLists,
    getDesserts,
    getDessertLists,
    getVariableDescriptions,
    getImageUrl,
};
